import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';

const sharedSessionSchema = z.object({
  id: z.string(),
  session_name: z.string(),
  phone_number: z.string().nullable(),
  status: z.string().nullable(),
  last_connected_at: z.string().nullable(),
  connected_by: z.string().nullable(),
  is_active: z.boolean().nullable(),
  provider_id: z.string().nullable(),
  provider: z.enum(['wppconnect', 'evolution']).nullable().default('wppconnect'),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});

export type SharedWhatsAppSession = z.infer<typeof sharedSessionSchema>;
export type WhatsAppProvider = 'wppconnect' | 'evolution';

async function fetchSharedSession(): Promise<SharedWhatsAppSession | null> {
  const { data, error } = await supabase
    .from('shared_whatsapp_session')
    .select('*')
    .eq('is_active', true)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const parsed = sharedSessionSchema.safeParse(data);
  if (!parsed.success) {
    console.error('Invalid shared session payload:', parsed.error.flatten());
    return null;
  }

  return parsed.data;
}

export function useSharedWhatsAppSession() {
  const queryClient = useQueryClient();
  const [isConnecting, setIsConnecting] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['sharedWhatsAppSession'],
    queryFn: fetchSharedSession,
    staleTime: 1000 * 10,
    gcTime: 1000 * 60 * 5,
  });

  const session = query.data ?? null;
  const currentProvider = session?.provider || 'wppconnect';

  // Get the correct edge function based on provider
  const getEdgeFunction = useCallback((provider?: WhatsAppProvider) => {
    const p = provider || currentProvider;
    return p === 'evolution' ? 'evolution-whatsapp' : 'wppconnect-whatsapp';
  }, [currentProvider]);

  const createSession = useMutation({
    mutationFn: async (sessionName: string): Promise<SharedWhatsAppSession> => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('shared_whatsapp_session')
        .insert({
          session_name: sessionName,
          connected_by: auth.user.id,
          status: 'disconnected',
          is_active: true,
          provider: 'wppconnect',
        })
        .select()
        .single();

      if (error) throw error;
      
      // Parse to ensure correct typing
      const parsed = sharedSessionSchema.safeParse(data);
      if (!parsed.success) {
        throw new Error('Invalid session data');
      }
      return parsed.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sharedWhatsAppSession'] });
    },
  });

  const updateSession = useMutation({
    mutationFn: async (updates: Partial<SharedWhatsAppSession>) => {
      if (!session?.id) throw new Error('No session to update');
      
      const { data, error } = await supabase
        .from('shared_whatsapp_session')
        .update(updates)
        .eq('id', session.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sharedWhatsAppSession'] });
    },
  });

  const setProvider = useMutation({
    mutationFn: async (provider: WhatsAppProvider) => {
      if (!session?.id) throw new Error('No session to update');

      // If connected, disconnect first
      if (session.status === 'connected') {
        const functionName = getEdgeFunction(session.provider || 'wppconnect');
        await supabase.functions.invoke(functionName, {
          body: { action: 'disconnectShared' },
        });
      }

      const { data, error } = await supabase
        .from('shared_whatsapp_session')
        .update({ 
          provider,
          status: 'disconnected',
          phone_number: null,
        })
        .eq('id', session.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setQrCode(null);
      queryClient.invalidateQueries({ queryKey: ['sharedWhatsAppSession'] });
    },
  });

  const connect = useCallback(async () => {
    setIsConnecting(true);
    setError(null);
    setQrCode(null);

    try {
      let currentSession = session;

      if (!currentSession) {
        const { data: auth } = await supabase.auth.getUser();
        if (!auth.user) throw new Error('Not authenticated');
        currentSession = await createSession.mutateAsync('shared_central');
      }

      const { data: tokenData } = await supabase.auth.getSession();
      const accessToken = tokenData.session?.access_token;
      
      if (!accessToken) throw new Error('No access token');

      await updateSession.mutateAsync({ status: 'connecting' });

      const provider = (currentSession?.provider as WhatsAppProvider) || 'wppconnect';
      const functionName = getEdgeFunction(provider);
      const response = await supabase.functions.invoke(functionName, {
        body: { action: 'startSharedSession' },
      });

      if (response.error) throw response.error;
      
      const result = response.data;
      
      if (result.qrCode) {
        setQrCode(result.qrCode);
      } else if (result.status === 'connected') {
        await updateSession.mutateAsync({
          status: 'connected',
          phone_number: result.phoneNumber || null,
          last_connected_at: new Date().toISOString(),
        });
      }

      return result;
    } catch (err) {
      console.error('Error connecting shared session:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect');
      throw err;
    } finally {
      setIsConnecting(false);
    }
  }, [session, createSession, updateSession, getEdgeFunction]);

  const disconnect = useCallback(async () => {
    if (!session) return;

    try {
      const functionName = getEdgeFunction();
      const response = await supabase.functions.invoke(functionName, {
        body: { action: 'disconnectShared' },
      });

      if (response.error) throw response.error;

      await updateSession.mutateAsync({
        status: 'disconnected',
        phone_number: null,
      });
    } catch (err) {
      console.error('Error disconnecting shared session:', err);
      setError(err instanceof Error ? err.message : 'Failed to disconnect');
      throw err;
    }
  }, [session, updateSession, getEdgeFunction]);

  const checkStatus = useCallback(async () => {
    if (!session) return null;

    try {
      const functionName = getEdgeFunction();
      const response = await supabase.functions.invoke(functionName, {
        body: { action: 'sharedStatus' },
      });

      if (response.error) throw response.error;
      
      const result = response.data;
      
      if (result.status === 'connected' && session.status !== 'connected') {
        await updateSession.mutateAsync({
          status: 'connected',
          phone_number: result.phoneNumber || null,
          last_connected_at: new Date().toISOString(),
        });
      } else if (result.qrCode) {
        setQrCode(result.qrCode);
      }

      return result;
    } catch (err) {
      console.error('Error checking shared session status:', err);
      return null;
    }
  }, [session, updateSession, getEdgeFunction]);

  return {
    session,
    loading: query.isLoading,
    isConnecting,
    qrCode,
    error,
    isConnected: session?.status === 'connected',
    phoneNumber: session?.phone_number ?? null,
    provider: currentProvider,
    connect,
    disconnect,
    checkStatus,
    setProvider: setProvider.mutateAsync,
    isChangingProvider: setProvider.isPending,
    refetch: query.refetch,
    setQrCode,
  };
}
